import { useTranslation } from 'react-i18next';
import React, { Fragment, useCallback } from 'react';
import cx from 'clsx';
import { useHistory } from 'react-router-dom';

import { IconRcArrowRightMin } from '@/assets';
import { IconRcStreamDrawHot } from '@/assets/stream';
import { RichTextView, Button, UserAvatar, UserName, UserCardPopup } from '@/components';
import { useSwitch, useCurrentAddressInfo, useDeviceTypeIsMobile, useTheme } from '@/hooks';
import { login, openInDebank, openNewWindow, L2TxReqParams } from '@/utils';

import { usePayQaWatch } from '../../hooks';
import { FeedArticle } from '../../types';
import { StreamInfiniteUsersModal } from '../OperationUserListModal';
import { PayArticleModal } from './PayArticleModal';
import style from './index.module.scss';
import { TagList } from '../TagList';

type ThreadContentProps = {
  article: FeedArticle;
  maxDisplay?: number;
  showRawImage?: boolean;
  maxImgSize?: [number, number];
  contentClassName?: string;
  enableTranslate?: boolean;
  isDetails?: boolean;
  disableOp?: boolean;
};

export const PayQaContent = (props: ThreadContentProps) => {
  const { t } = useTranslation();

  const {
    article,
    maxDisplay,
    showRawImage,
    maxImgSize,
    contentClassName,
    enableTranslate,
    isDetails,
  } = props;

  const isMobile = useDeviceTypeIsMobile();
  const { isVerified, address } = useCurrentAddressInfo();
  const { on, turnOn, turnOff } = useSwitch();
  const { on: isPaidUserShow, turnOn: showPaidUser, turnOff: hidePaidUser } = useSwitch();
  const { isDark } = useTheme();
  const { mutateAsync } = usePayQaWatch(article.id);
  const history = useHistory();

  const threadTotalCount = article.thread_count || 0;
  const isValidDetail = article.is_visible && isDetails;

  const handlePayArticleClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (isMobile) {
        openInDebank({ link: `/stream/${article.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }

      try {
        turnOn();
      } catch (error) {}
    },
    [isMobile, isVerified, turnOn, article.id],
  );

  const handlePayUsersClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      showPaidUser();
    },
    [showPaidUser],
  );

  const isQuestioner = article.creator_id === address;
  const isAnswer = article.pay_qa?.answerer?.id === address;
  const isRespond = !!article.thread_count;

  const isPaidOrSelf = article.pay_qa?.is_paid || isQuestioner || isAnswer;

  const openAnswerProfile = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      openNewWindow(`/profile/${article.pay_qa?.answerer?.id}/stream`, '_blank');
    },
    [article.pay_qa?.answerer?.id],
  );

  const handleSubmit = useCallback(
    async (tx: L2TxReqParams) => {
      try {
        await mutateAsync(tx);

        if (!isDetails) {
          history.push(`/stream/${article.id}`);
        }
      } catch (error) {}
    },
    [mutateAsync, history, article.id, isDetails],
  );

  return (
    <>
      {!isDetails && isRespond ? (
        <div className={style.threadLineWrap}>
          <div className={style.threadLine} />
          <UserAvatar
            data={article.pay_qa?.answerer?.desc}
            size={48}
            userCardPopup
            onClick={openAnswerProfile}
          />
        </div>
      ) : null}
      <RichTextView
        article={article}
        maxDisplay={maxDisplay}
        showRawImage={showRawImage}
        maxImgSize={maxImgSize}
        className={cx(style.qaContent, contentClassName)}
        enableTranslate={enableTranslate}
        isDetails={isDetails}
      />
      {isValidDetail && isRespond ? (
        <div className={style.replyTitle}>
          <div className={style.answer}>
            <UserAvatar
              data={article?.pay_qa?.answerer?.desc}
              size={48}
              userCardPopup
              onClick={openAnswerProfile}
            />
            <div className={style.userInfo}>
              <UserCardPopup address={article.pay_qa?.answerer?.id!}>
                <UserName
                  name={article.pay_qa?.answerer?.web3_id}
                  address={article.pay_qa?.answerer?.id}
                  className={style.userName}
                  onClick={openAnswerProfile}
                  userDesc={article.pay_qa?.answerer?.desc}
                />
              </UserCardPopup>
              <div className={style.repliedTag}>{t('ArticleContent_PayQaContent.1ru6bct')}</div>
            </div>
          </div>
          <div className={style.paidUser}>
            <IconRcStreamDrawHot className={style.hotIcon} />
            <span className={style.userCount} onClick={handlePayUsersClick}>
              {article.pay_qa?._payCount}{' '}
              {t('ArticleContent_PayQaContent.4nd9j3', { count: article.pay_qa?.pay_count! })}
            </span>
          </div>
        </div>
      ) : null}
      {isValidDetail && isPaidOrSelf && isRespond ? (
        <>
          {article.threads!.map((x, i) => (
            <Fragment key={x.id}>
              <div className={style.threadSeg}>
                {i + 1}/{threadTotalCount}
              </div>
              <RichTextView
                article={x as any}
                maxDisplay={maxDisplay}
                showRawImage={showRawImage}
                maxImgSize={maxImgSize}
                className={contentClassName}
                enableTranslate={enableTranslate}
                isDetails={isDetails}
              />
            </Fragment>
          ))}
        </>
      ) : null}
      {isValidDetail && isRespond && !isPaidOrSelf ? (
        <div className={cx(style.unpaidBlur, style.isQa, isDark && style.isDark)}>
          <Button type="primary" className={style.payBtn} onClick={handlePayArticleClick}>
            {article.pay_qa?._price} {t('ArticleContent_PayQaContent.1d0omvj')}
          </Button>
          <div className={style.payQaDesc}>
            {article.pay_qa?._price} {t('ArticleContent_PayQaContent.1vrwprh')}
          </div>
        </div>
      ) : null}
      <TagList article={article} />
      {!isDetails && isRespond ? (
        <div className={cx(style.payThreadFooter, isDetails && style.isDetail)}>
          <div className={style.threadStatus}>
            <div className={style.repliedTag}>{t('ArticleContent_PayQaContent.1ru6bct')}</div>
            {isAnswer || isQuestioner ? (
              <div className={style.viewThreadLink}>
                {t('ArticleContent_PayQaContent.15yya8k')}
                <IconRcArrowRightMin />
              </div>
            ) : article?.pay_qa?.is_paid ? (
              <div className={cx(style.payThreadInfo, style.isPaid)}>
                {t('ArticleContent_PayQaContent.ff74ev', { price: article.pay_qa?._price })}
                <IconRcArrowRightMin className={style.payFooterArrow} />
              </div>
            ) : (
              <div className={style.payThreadInfo} onClick={handlePayArticleClick}>
                {t('ArticleContent_PayQaContent.1pqg', { price: article?.pay_qa?._price })}
                <IconRcArrowRightMin />
              </div>
            )}
          </div>
          <div className={style.paidUser}>
            <IconRcStreamDrawHot className={style.hotIcon} />
            <span className={style.userCount} onClick={handlePayUsersClick}>
              {article.pay_qa?._payCount}{' '}
              {t('ArticleContent_PayQaContent.4nd9j3', { count: article.pay_qa?.pay_count! })}
            </span>
          </div>
        </div>
      ) : null}
      {on ? (
        <PayArticleModal
          turnOff={turnOff}
          articleId={article.id}
          message={t('ArticleContent_PayQaContent.reply')}
          price={article.pay_qa?.price}
          onSubmit={handleSubmit}
        />
      ) : null}
      {isPaidUserShow ? (
        <StreamInfiniteUsersModal
          title={`${article.pay_qa?._payCount} ${t('ArticleContent_ThreadContent.4nd9j3', {
            count: article.pay_count!,
          })}`}
          type="payQa"
          on={isPaidUserShow}
          turnOff={hidePaidUser}
          articleId={article.id}
        />
      ) : null}
    </>
  );
};
